/* GENERATED FILE — do not edit.
   Source: shared-ui/ui/VideoModal.js
   Run "node shared-ui/sync.js" after editing the source. */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import videojs from 'video.js'
import 'video.js/dist/video-js.css'

import Avatar from './Avatar'
import { videoCandidates, posterUrl } from './videoSource'
import './videoModal.css'

/*
 * The one video surface in the manager — used by the challenge leaderboard and by a
 * player's own activity list.
 *
 * Built on video.js 8, which is already a direct dependency. The old
 * react-video-js-player wrapper targets the video.js 6/7 API and is unmaintained.
 *
 * What a scout needs that the old modal did not give them:
 *   - whose clip this is, and what they scored, without closing it
 *   - the clip framed portrait-first (they are filmed on phones), no black letterbox
 *   - playback speed and frame stepping, to actually read technique
 *   - next / previous through the other entries without going back to the list
 *
 * props
 *   items    [{ fileId, name, uuid, score, scoreLabel, subtitle }]
 *   index    which item is showing
 *   title    the context line (e.g. the challenge name)
 *   onIndex  (nextIndex) => void
 *   onClose  () => void
 */

const RATES = [0.25, 0.5, 1, 1.5, 2]
const FRAME = 1 / 30

export default function VideoModal({ items, index, title, onIndex, onClose }) {
  const list = Array.isArray(items) ? items.filter(Boolean) : []
  const safeIndex = Math.max(0, Math.min(list.length - 1, Number(index) || 0))
  const item = list[safeIndex]

  const hostRef = useRef(null)
  const playerRef = useRef(null)
  const dialogRef = useRef(null)

  const [rate, setRate] = useState(1)
  const [failed, setFailed] = useState(false)
  // Which naming convention we are currently trying for this clip.
  const [attempt, setAttempt] = useState(0)
  // Clips are a mix of portrait phone footage and landscape hall footage.
  // Portrait is the assumption until the file tells us otherwise.
  const [ratio, setRatio] = useState('9 / 16')

  const sources = useMemo(() => (item ? videoCandidates(item.fileId) : []), [item])
  const src = sources[attempt] || ''
  const poster = useMemo(() => (item ? posterUrl(item.fileId) : ''), [item])

  // A new clip starts the walk again.
  useEffect(() => { setAttempt(0); setRatio('9 / 16') }, [item])

  const go = useCallback(
    (delta) => {
      if (list.length < 2) return
      const next = (safeIndex + delta + list.length) % list.length
      onIndex(next)
    },
    [safeIndex, list.length, onIndex]
  )

  /* Create the player once; swap sources as the scout moves between entries. */
  useEffect(() => {
    if (!hostRef.current || playerRef.current) return undefined

    const el = document.createElement('video-js')
    el.classList.add('vjs-big-play-centered', 'vm-video')
    hostRef.current.appendChild(el)

    const player = videojs(el, {
      controls: true,
      preload: 'auto',
      fluid: false,
      responsive: true,
      playbackRates: RATES,
      html5: { vhs: { overrideNative: true } },
    })

    playerRef.current = player
    return undefined
  }, [])

  /* Tear the player down only when the modal itself goes away. */
  useEffect(
    () => () => {
      if (playerRef.current) {
        playerRef.current.dispose()
        playerRef.current = null
      }
    },
    []
  )

  useEffect(() => {
    const player = playerRef.current
    if (!player || !src) return

    setFailed(false)
    if (attempt === 0) setRate(1)

    player.poster(poster || '')
    player.src({ src, type: 'application/x-mpegURL' })
    // A fallback retry keeps the speed the scout chose; a new clip starts at 1x.
    player.playbackRate(attempt === 0 ? 1 : rate)

    // Not every clip has every rendition — walk the candidates before giving up.
    const onError = () => {
      if (attempt < sources.length - 1) {
        setAttempt((n) => n + 1)
      } else {
        setFailed(true)
      }
    }
    player.on('error', onError)

    // Size the frame to the actual video so neither orientation gets letterboxed.
    const onMeta = () => {
      const w = player.videoWidth()
      const h = player.videoHeight()
      if (w > 0 && h > 0) setRatio(`${w} / ${h}`)
    }
    player.on('loadedmetadata', onMeta)

    const play = player.play()
    if (play && typeof play.catch === 'function') {
      // Autoplay is blocked until the viewer interacts — the poster stays up, which is fine.
      play.catch(() => {})
    }

    return () => {
      player.off('error', onError)
      player.off('loadedmetadata', onMeta)
    }
  }, [src, poster, attempt, sources.length])

  /* Escape closes, arrows move between entries. */
  useEffect(() => {
    const onKey = (ev) => {
      if (ev.key === 'Escape') {
        ev.stopPropagation()
        onClose()
        return
      }
      if (ev.key === 'ArrowRight') go(1)
      if (ev.key === 'ArrowLeft') go(-1)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [go, onClose])

  useEffect(() => {
    const node = dialogRef.current
    if (node) node.focus()
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [])

  const setSpeed = (value) => {
    setRate(value)
    if (playerRef.current) playerRef.current.playbackRate(value)
  }

  const step = (direction) => {
    const player = playerRef.current
    if (!player) return
    player.pause()
    player.currentTime(Math.max(0, player.currentTime() + direction * FRAME))
  }

  if (!item) return null

  return createPortal(
    <div className="vm-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose() }}>
      <div
        className="vm-dialog"
        role="dialog"
        aria-modal="true"
        aria-label={`${item.name || 'Player'} — video`}
        tabIndex={-1}
        ref={dialogRef}
      >
        <div className="vm-head">
          <Avatar uuid={item.uuid} name={item.name} size={40} />

          <div className="vm-who">
            <div className="vm-name">{item.name || 'Unknown player'}</div>
            {(item.subtitle || title) && (
              <div className="vm-sub">{item.subtitle || title}</div>
            )}
          </div>

          {item.score !== undefined && item.score !== null && item.score !== '' && (
            <div className="vm-score">
              <b className="num">{item.score}</b>
              <span className="mono">{item.scoreLabel || 'pts'}</span>
            </div>
          )}

          <button type="button" className="vm-close" onClick={onClose} aria-label="Close video">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </div>

        <div className="vm-stage">
          {list.length > 1 && (
            <button type="button" className="vm-nav vm-prev" onClick={() => go(-1)} aria-label="Previous player">
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15 6l-6 6 6 6" /></svg>
            </button>
          )}

          <div className="vm-frame" ref={hostRef} style={{ aspectRatio: ratio }} />

          {failed && (
            <div className="vm-failed">
              <b>This clip could not be loaded.</b>
              <span>The video may still be processing on the server. Try another entry.</span>
            </div>
          )}

          {list.length > 1 && (
            <button type="button" className="vm-nav vm-next" onClick={() => go(1)} aria-label="Next player">
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 6l6 6-6 6" /></svg>
            </button>
          )}
        </div>

        <div className="vm-foot">
          <div className="vm-speed" role="group" aria-label="Playback speed">
            <span className="mono vm-foot-label">Speed</span>
            {RATES.map((value) => (
              <button
                key={value}
                type="button"
                className="vm-chip"
                aria-pressed={rate === value}
                onClick={() => setSpeed(value)}
              >
                {value}×
              </button>
            ))}
          </div>

          <div className="vm-steps" role="group" aria-label="Step frame">
            <span className="mono vm-foot-label">Frame</span>
            <button type="button" className="vm-chip" onClick={() => step(-1)} aria-label="Previous frame">−1</button>
            <button type="button" className="vm-chip" onClick={() => step(1)} aria-label="Next frame">+1</button>
          </div>

          {list.length > 1 && (
            <div className="vm-count mono">
              {safeIndex + 1} / {list.length}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  )
}
